import { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  CloudOff,
  Loader2,
  RectangleHorizontal,
  RectangleVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateCollection } from "@/components/records/report-generation-dialog/use-create-collection";
import type {
  ReportFormat,
  ReportOrientation,
} from "@/components/records/report-generation-dialog/use-generate-report";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useOnlineStatus } from "@/offline/online-status";
import { createErrorMessage } from "./create-error-message";
import {
  readIssuesReportSettings,
  saveIssuesReportSettings,
  type IssuesReportSettings,
} from "./issues-report-settings";

const FORMAT_OPTIONS: ReadonlyArray<[ReportFormat, string]> = [
  ["PDF", "PDF"],
  ["PPTX", "PowerPoint"],
];

interface IssuesReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  /** The ticked issues. The dialog never opens for none. */
  issueIds: string[];
  /**
   * The report name, owned by the page so it survives closing the dialog to
   * tick one more issue. See [[issues-report-dialog.spec]] § The name is the
   * page's, not the dialog's.
   */
  name: string;
  /** The name is the untouched prefill, so focusing the input selects it. */
  isPrefilledName: boolean;
  onNameChange: (name: string) => void;
  /** The report exists; the page ends selection and goes to Reports. */
  onGenerated: () => void;
}

/**
 * Turns the ticked issues into a report: a name, a format, an orientation and
 * an order, and nothing else — an issues report has one layout, no remarks and
 * no history choice. A centred dialog on desktop, a bottom sheet on a phone.
 * See [[issues-report-dialog.spec]].
 */
export function IssuesReportDialog({
  open,
  onOpenChange,
  projectId,
  issueIds,
  name,
  isPrefilledName,
  onNameChange,
  onGenerated,
}: IssuesReportDialogProps) {
  const isDesktop = useIsDesktop();
  const createReport = useCreateCollection(projectId);
  const isGenerating = createReport.isPending;

  // Closing mid-request would orphan it, and the dialog is where its answer
  // lands, so every dismissal waits for it.
  const handleOpenChange = (next: boolean) => {
    if (!next && isGenerating) return;
    onOpenChange(next);
  };

  // Keyed on `open` so the settings are re-read and a stale error cleared each
  // time the dialog opens.
  const form = (
    <IssuesReportForm
      key={open ? "open" : "closed"}
      presentation={isDesktop ? "dialog" : "drawer"}
      issueIds={issueIds}
      name={name}
      isPrefilledName={isPrefilledName}
      onNameChange={onNameChange}
      isGenerating={isGenerating}
      onCancel={() => handleOpenChange(false)}
      onGenerate={(settings, onError) =>
        createReport.mutate(
          {
            kind: "issues",
            issueIds,
            name: name.trim(),
            format: settings.format,
            orientation: settings.orientation,
          },
          { onSuccess: onGenerated, onError },
        )
      }
    />
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {/* `bare`: the form lays out its own header, body and footer. */}
        <DialogContent bare showCloseButton={!isGenerating}>
          {form}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      {/* No autofocus on a phone: focusing the name on open would throw the
          keyboard over the sheet before the user has read it. */}
      <DrawerContent
        className="max-h-[90vh]"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {form}
      </DrawerContent>
    </Drawer>
  );
}

interface IssuesReportFormProps {
  presentation: "dialog" | "drawer";
  issueIds: string[];
  name: string;
  isPrefilledName: boolean;
  onNameChange: (name: string) => void;
  isGenerating: boolean;
  onCancel: () => void;
  onGenerate: (
    settings: IssuesReportSettings,
    onError: (error: Error) => void,
  ) => void;
}

function IssuesReportForm({
  presentation,
  issueIds,
  name,
  isPrefilledName,
  onNameChange,
  isGenerating,
  onCancel,
  onGenerate,
}: IssuesReportFormProps) {
  const isOnline = useOnlineStatus();
  const [settings, setSettings] = useState(readIssuesReportSettings);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set in the click itself: the mutation's own pending flag reaches this
  // render a tick later, and a quick double tap must still send one request.
  const [submitted, setSubmitted] = useState(false);
  const busy = isGenerating || submitted;

  // The title and line must belong to whichever primitive hosts the form —
  // each wires its own to its accessible name and description.
  const isDialog = presentation === "dialog";
  const Header = isDialog ? DialogHeader : DrawerHeader;
  const Title = isDialog ? DialogTitle : DrawerTitle;
  const Description = isDialog ? DialogDescription : DrawerDescription;

  const update = (patch: Partial<IssuesReportSettings>) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveIssuesReportSettings(next);
  };

  const count = issueIds.length;
  const canGenerate =
    count > 0 && name.trim().length > 0 && isOnline && !busy;

  const submit = () => {
    if (!canGenerate) return;
    setSubmitted(true);
    setErrorMessage(null);
    onGenerate(settings, (error) => {
      setSubmitted(false);
      // A 4xx is the server's answer about this request and is shown, not
      // reported; anything else is unexpected. See error-reporting.md.
      if (!(error instanceof ApiError) || error.status >= 500) {
        Sentry.captureException(error);
      }
      setErrorMessage(createErrorMessage(error));
    });
  };

  return (
    <>
      <Header
        className={cn(
          "shrink-0 text-left",
          isDialog ? "border-b px-6 py-4 pr-12" : "px-4 pb-3",
        )}
      >
        <Title>Generate Report</Title>
        <Description className="text-sm text-muted-foreground">
          {count} {count === 1 ? "issue" : "issues"}. Each shows its current
          status and latest update.
        </Description>
      </Header>

      <div
        className={cn(
          "flex min-h-0 flex-col gap-4 overflow-y-auto",
          isDialog ? "px-6 py-4" : "px-4 pb-4",
        )}
      >
        <div className="space-y-2">
          <Label htmlFor="issues-report-name">Report Name</Label>
          <Input
            id="issues-report-name"
            placeholder="Enter report name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            // The untouched prefill is selected on focus, so typing replaces
            // it rather than appending to it.
            onFocus={(event) => {
              if (isPrefilledName) event.currentTarget.select();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
            }}
            disabled={busy}
            required
            aria-required="true"
          />
        </div>

        <ToggleRow
          label="Format"
          options={FORMAT_OPTIONS}
          value={settings.format}
          onChange={(format) => update({ format })}
          disabled={busy}
        />

        {/* A deck is always landscape, so orientation is PDF-only. */}
        {settings.format === "PDF" && (
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">Orientation</Label>
            <div className="flex gap-1">
              <OrientationButton
                value="PORTRAIT"
                current={settings.orientation}
                onSelect={(orientation) => update({ orientation })}
                disabled={busy}
              />
              <OrientationButton
                value="LANDSCAPE"
                current={settings.orientation}
                onSelect={(orientation) => update({ orientation })}
                disabled={busy}
              />
            </div>
          </div>
        )}

        {!isOnline && (
          <div className="flex items-start gap-3 rounded-md border border-border bg-muted px-4 py-3 text-muted-foreground">
            <CloudOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-sm">
              You&apos;re offline. Reconnect to generate this report — your
              selection is saved.
            </span>
          </div>
        )}

        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>

      <div
        className={cn(
          "flex shrink-0 gap-2 border-t",
          isDialog
            ? "justify-end bg-muted/10 px-6 py-4"
            : "pb-sheet-actions border-border p-4",
        )}
      >
        <Button
          variant="outline"
          className={isDialog ? undefined : "basis-[38%]"}
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          className={isDialog ? undefined : "flex-1"}
          onClick={submit}
          disabled={!canGenerate}
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Generating…
            </>
          ) : (
            "Generate"
          )}
        </Button>
      </div>
    </>
  );
}

/**
 * Two equal toggle buttons: the photos dialog's Format control, so the two
 * dialogs look alike.
 */
function ToggleRow<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: ReadonlyArray<[T, string]>;
  value: T;
  onChange: (value: T) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2" role="group" aria-label={label}>
        {options.map(([option, text]) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={value === option}
            disabled={disabled}
            className={cn(
              "flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50",
              value === option
                ? "border-2 border-primary bg-primary/5 text-primary"
                : "border border-muted text-muted-foreground hover:border-muted-foreground/30",
            )}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function OrientationButton({
  value,
  current,
  onSelect,
  disabled,
}: {
  value: ReportOrientation;
  current: ReportOrientation;
  onSelect: (value: ReportOrientation) => void;
  disabled: boolean;
}) {
  const portrait = value === "PORTRAIT";
  const Icon = portrait ? RectangleVertical : RectangleHorizontal;
  return (
    <Button
      type="button"
      variant={current === value ? "default" : "outline"}
      size="icon"
      className="h-11 w-11"
      onClick={() => onSelect(value)}
      aria-label={portrait ? "Portrait orientation" : "Landscape orientation"}
      aria-pressed={current === value}
      disabled={disabled}
    >
      <Icon className="h-5 w-5" />
    </Button>
  );
}
